import { Fragment, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import {
  Alert,
  Button,
  Chip,
  Collapse,
  IconButton,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { usePriceResolution, useRetailStores } from '../../hooks';
import { formatPrice } from '../../lib/format';
import { summarizeStoreCoverage } from '../../lib/priceListCoverage';
import type {
  PriceListBindingResponse,
  PriceListResponse,
  StoreCoverageRow,
} from '../../model/priceListTypes';
import type { PriceLineResponse } from '../../model/types';
import { PriceListRetagChecklistDrawer } from './PriceListRetagChecklistDrawer';

export interface PriceListCoveragePanelProps {
  lists: PriceListResponse[];
  bindingsByCode: Map<string, PriceListBindingResponse[]>;
  linesByCode: Map<string, PriceLineResponse[]>;
  assortmentTotal: number | null;
  seedStore: string | null;
  overviewLoading: boolean;
  overviewError: boolean;
  onRetry: () => void;
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/** The winning rung's label, derived from which resolution field is set (ladder priority order). */
function resolutionSourceLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  row: { priceListCodeUsed?: string; channelCodeUsed?: string; groupCodeUsed?: string },
  listName?: string,
): string {
  if (row.priceListCodeUsed) {
    return t('priceListCoverage.resolutionCheck.sourceList', { name: listName ?? row.priceListCodeUsed });
  }
  if (row.channelCodeUsed) {
    return t('priceListCoverage.resolutionCheck.sourceChannel', { code: row.channelCodeUsed });
  }
  if (row.groupCodeUsed) {
    return t('priceListCoverage.resolutionCheck.sourceGroup', { code: row.groupCodeUsed });
  }
  return t('priceListCoverage.resolutionCheck.sourceBase');
}

/** One store's inline resolution-check (design spec §8.4) — a simplified, honest rendering of the
 * winning rung only: `GET price-resolution` returns the resolved amount + WHICH rung matched, not
 * a full per-rung breakdown (no "what would rung 2/5 have been" data exists), so this shows the
 * verdict, not the mockup's fuller multi-row ladder walk. */
function ResolutionCheckRow({ storeCode, lists }: { storeCode: string; lists: PriceListResponse[] }) {
  const { t } = useTranslation('catalog');
  const [productIdInput, setProductIdInput] = useState('');
  const [checkedProductId, setCheckedProductId] = useState<string | null>(null);

  const resolution = usePriceResolution(
    checkedProductId ? { productId: checkedProductId, storeCode } : undefined,
    { enabled: Boolean(checkedProductId) },
  );

  return (
    <Stack spacing={1.5} sx={{ py: 2, px: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t('priceListCoverage.resolutionCheck.title')}
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <TextField
          size="small"
          label={t('priceListCoverage.resolutionCheck.productIdLabel')}
          value={productIdInput}
          onChange={(event) => setProductIdInput(event.target.value)}
          slotProps={{ htmlInput: { style: { fontFamily: '"JetBrains Mono", monospace' } } }}
          sx={{ minWidth: 320 }}
        />
        <Button
          variant="outlined"
          disabled={productIdInput.trim() === ''}
          onClick={() => setCheckedProductId(productIdInput.trim())}
        >
          {t('priceListCoverage.resolutionCheck.showAction')}
        </Button>
      </Stack>
      {!checkedProductId ? (
        <Typography variant="body2" color="text.secondary">
          {t('priceListCoverage.resolutionCheck.idleHint')}
        </Typography>
      ) : resolution.isLoading ? (
        <LoadingState label={t('priceListCoverage.resolutionCheck.loading')} fullHeight={false} />
      ) : resolution.isError ? (
        <ErrorState
          message={resolution.error?.message ?? t('priceListCoverage.resolutionCheck.error')}
          onRetry={() => void resolution.refetch()}
        />
      ) : resolution.data ? (
        <Alert severity="success" icon={false} sx={{ bgcolor: 'action.hover' }}>
          <Typography variant="body2">
            {t('priceListCoverage.resolutionCheck.result', {
              store: storeCode,
              amount: formatPrice(resolution.data.unitAmount),
              source: resolutionSourceLabel(
                t,
                resolution.data,
                lists.find((list) => list.code === resolution.data?.priceListCodeUsed)?.name,
              ),
            })}
          </Typography>
        </Alert>
      ) : null}
    </Stack>
  );
}

/** Store-coverage sub-view (design spec §8.4) — every directory store, bound or not. */
export function PriceListCoveragePanel({
  lists,
  bindingsByCode,
  linesByCode,
  assortmentTotal,
  seedStore,
  overviewLoading,
  overviewError,
  onRetry,
}: PriceListCoveragePanelProps) {
  const { t } = useTranslation('catalog');
  const storesQuery = useRetailStores();
  const [expanded, setExpanded] = useState<string | null>(seedStore);
  const [dismissedNotice, setDismissedNotice] = useState(false);
  const [retagTarget, setRetagTarget] = useState<StoreCoverageRow | null>(null);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());

  useEffect(() => {
    if (!seedStore) return;
    setExpanded(seedStore);
    const el = rowRefs.current.get(seedStore);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [seedStore]);

  if (overviewLoading || storesQuery.isLoading) {
    return <LoadingState label={t('priceListCoverage.loading')} />;
  }
  if (overviewError || storesQuery.isError) {
    return <ErrorState message={t('priceListCoverage.loadError')} onRetry={onRetry} />;
  }

  const stores = storesQuery.data ?? [];
  if (stores.length === 0) {
    return (
      <EmptyState
        icon={<StorefrontRoundedIcon sx={{ fontSize: 48 }} />}
        title={t('priceListCoverage.noStores.title')}
        description={t('priceListCoverage.noStores.description')}
        action={
          <Button component={RouterLink} to="/settings/retail-evotor" variant="outlined">
            {t('priceListCoverage.noStores.action')}
          </Button>
        }
      />
    );
  }

  const rows = summarizeStoreCoverage(stores, lists, bindingsByCode, linesByCode, assortmentTotal);
  const anyBound = rows.some((row) => row.binding !== null);

  return (
    <Stack spacing={2}>
      {!anyBound && !dismissedNotice ? (
        <Alert severity="info" onClose={() => setDismissedNotice(true)}>
          {t('priceListCoverage.noBindingsNotice')}
        </Alert>
      ) : null}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('priceListCoverage.table.store')}</TableCell>
              <TableCell>{t('priceListCoverage.table.priceList')}</TableCell>
              <TableCell>{t('priceListCoverage.table.coverage')}</TableCell>
              <TableCell>{t('priceListCoverage.table.fallthrough')}</TableCell>
              <TableCell>{t('priceListCoverage.table.nextChange')}</TableCell>
              <TableCell align="right">{t('priceListCoverage.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const isExpanded = expanded === row.store.code;
              return (
                <Fragment key={row.store.code}>
                  <TableRow
                    hover
                    ref={(el) => {
                      if (el) rowRefs.current.set(row.store.code, el);
                      else rowRefs.current.delete(row.store.code);
                    }}
                    sx={row.store.code === seedStore ? { bgcolor: 'action.selected' } : undefined}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.store.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
                        {row.store.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.list ? (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <MuiLink
                            component={RouterLink}
                            to={`/catalog/pricing/price-lists/${encodeURIComponent(row.list.code)}`}
                          >
                            {row.list.name}
                          </MuiLink>
                          {!row.list.active ? (
                            <Tooltip title={t('priceListCoverage.inactiveListTooltip')}>
                              <Chip size="small" label={t('priceListCoverage.inactiveListChip')} />
                            </Tooltip>
                          ) : null}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('priceListDetail.bindings.notBound')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.coveragePercent === null ? (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        `${row.coveragePercent} %`
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t('priceListCoverage.fallthroughTooltip')}>
                        <Typography variant="body2" color="text.secondary" component="span">
                          {t('priceListCoverage.fallthrough')}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.nextChangeAt ? formatDateTime(row.nextChangeAt) : '—'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {row.nextChangeAt ? (
                          <Button size="small" onClick={() => setRetagTarget(row)}>
                            {t('priceListCoverage.retagButton')}
                          </Button>
                        ) : null}
                        <Tooltip title={t('priceListCoverage.retailSettingsLink')}>
                          <IconButton
                            size="small"
                            component={RouterLink}
                            to="/settings/retail-evotor"
                            aria-label={t('priceListCoverage.retailSettingsLink')}
                          >
                            <SettingsRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          aria-label={t('priceListCoverage.expandAria', { store: row.store.name })}
                          aria-expanded={isExpanded}
                          onClick={() => setExpanded(isExpanded ? null : row.store.code)}
                          sx={{ transform: isExpanded ? 'rotate(180deg)' : undefined }}
                        >
                          <ExpandMoreRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                      <Collapse in={isExpanded} unmountOnExit>
                        <ResolutionCheckRow storeCode={row.store.code} lists={lists} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <PriceListRetagChecklistDrawer
        open={retagTarget !== null}
        storeName={retagTarget?.store.name ?? ''}
        lines={retagTarget?.list ? (linesByCode.get(retagTarget.list.code) ?? []) : []}
        validFrom={retagTarget?.nextChangeAt ?? null}
        onClose={() => setRetagTarget(null)}
      />
    </Stack>
  );
}
